import { useMemo, useState, type ReactElement } from 'react';

import { CatalogSelect } from '@/components/ui/CatalogSelect';
import { cn } from '@/components/ui/cn';
import { normBadgeClass } from '@/lib/norm-badge';

import { ENTITY_COPY } from '../copy';
import type { NormClauseRow } from '../types';
import { filterByStatus, type UseCatalogView } from '../use-catalog-view';
import { useCatalogRows } from '../use-catalogs-admin';
import { useToggleActive } from '../use-toggle-active';
import { CatalogBody } from './CatalogBody';
import { CatalogToolbar } from './CatalogToolbar';
import { NormModal } from './NormModal';
import { ActiveBadge, RowActions } from './RowActions';

const COPY = ENTITY_COPY.norm;
const NATURAL = new Intl.Collator('es', { numeric: true });
const labelOf = (n: NormClauseRow): string => `${n.normCode} ${n.clause}`;

interface Props {
  view: UseCatalogView;
  creating: boolean;
  onCreate: () => void;
  onCloseCreate: () => void;
}

/** Normas y sus cláusulas (multinorma, reunión F1): ordenadas por norma y cláusula
 *  en orden natural, con filtro por norma para preparar una auditoría. */
export function NormsPanel({ view, creating, onCreate, onCloseCreate }: Props): ReactElement {
  const query = useCatalogRows('norm-catalog', { enabled: true });
  const toggle = useToggleActive('norm-catalog', 'norm', labelOf);
  const [editing, setEditing] = useState<NormClauseRow | null>(null);
  const { status, norm } = view.view;
  const all = query.data;
  const normCodes = useMemo(
    () => [...new Set((all ?? []).map((n) => n.normCode))].sort((a, b) => NATURAL.compare(a, b)),
    [all],
  );
  const rows = filterByStatus(all ?? [], status)
    .filter((n) => norm === null || n.normCode === norm)
    .sort((a, b) => NATURAL.compare(a.normCode, b.normCode) || NATURAL.compare(a.clause, b.clause));

  return (
    <>
      <CatalogToolbar status={status} onStatus={view.setStatus}>
        <CatalogSelect
          label="Norma"
          allLabel="Todas"
          value={norm}
          items={normCodes.map((c) => ({ id: c, name: c, isActive: true }))}
          onChange={view.setNorm}
        />
      </CatalogToolbar>
      <CatalogBody
        copy={COPY}
        query={query}
        visible={rows.length}
        status={status}
        extraFilter={norm !== null}
        onCreate={onCreate}
        onClearFilters={() => {
          view.setNorm(null);
          view.setStatus('todos');
        }}
      >
        <div className="t-scroll">
          <table className="t t-cards">
            <caption className="sr-only">Normas y cláusulas de la empresa activa</caption>
            <thead>
              <tr>
                <th scope="col">Norma</th>
                <th scope="col">Cláusula</th>
                <th scope="col">Requisito</th>
                <th scope="col">Estado</th>
                <th scope="col" className="t-act">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id} aria-label={labelOf(n)}>
                  <td data-label="Norma">
                    <span className={cn('badge', normBadgeClass(n.normCode))}>{n.normCode}</span>
                  </td>
                  <td data-label="Cláusula" className="code">
                    {n.clause}
                  </td>
                  <td data-label="Requisito">{n.title}</td>
                  <td data-label="Estado">
                    <ActiveBadge active={n.isActive} />
                  </td>
                  <td data-label="Acciones" className="t-act">
                    <RowActions
                      label={labelOf(n)}
                      active={n.isActive}
                      busy={toggle.isBusy(n.id)}
                      onEdit={() => setEditing(n)}
                      onToggle={() => toggle.request(n)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CatalogBody>
      <NormModal
        open={creating || editing !== null}
        editing={editing}
        normCodes={normCodes}
        onClose={() => {
          setEditing(null);
          onCloseCreate();
        }}
      />
      {toggle.confirm}
    </>
  );
}
